import React from 'react';
import { CheckCircle, Clock, Star, ArrowRight, Phone, MessageSquare } from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';

interface UniversalSessionEndedProps {
  customerName?: string;
  agentName?: string;
  sessionDuration?: string;
  companyName?: string;
}

export const UniversalSessionEnded: React.FC<UniversalSessionEndedProps> = ({
  customerName,
  agentName,
  sessionDuration,
  companyName
}) => {
  // Parse URL parameters for fallback values
  const urlParams = new URLSearchParams(window.location.search);
  const finalCustomerName = customerName || urlParams.get('customerName') || 'Customer';
  const finalAgentName = agentName || urlParams.get('agentName') || 'Support Agent';
  const finalCompanyName = companyName || urlParams.get('companyName') || 'Universal Support';
  
  const handleFeedback = (rating: number) => {
    // This would typically send feedback to an API
    console.log(`Customer rated session: ${rating} stars`);
    alert(`Thank you for rating our service ${rating} star${rating !== 1 ? 's' : ''}!`);
  };

  return (
    <BrandingProvider>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 py-8 max-w-md">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Session Completed!
            </h1>
            <p className="text-gray-600">
              Thank you for using {finalCompanyName}
            </p>
          </div>

          {/* Session Summary Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Summary</h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Customer</span>
                <span className="font-medium text-gray-900">{finalCustomerName}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Support Agent</span>
                <span className="font-medium text-gray-900">{finalAgentName}</span>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600 text-sm">Company</span>
                <span className="font-medium text-gray-900">{finalCompanyName}</span>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-600 text-sm">Session Time</span>
                <div className="flex items-center text-gray-900 font-medium">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{sessionDuration || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Rating Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              How was your experience?
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Your feedback helps us improve our service
            </p>
            
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => handleFeedback(rating)}
                  className="p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <Star 
                    className="w-8 h-8 text-gray-300 group-hover:text-yellow-400 transition-colors cursor-pointer"
                    fill="none"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What's Next?
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    Follow-up Communication
                  </p>
                  <p className="text-xs text-blue-700">
                    We may contact you with updates or additional information
                  </p>
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-green-50 rounded-lg">
                <Phone className="w-5 h-5 text-green-600 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    Need More Help?
                  </p>
                  <p className="text-xs text-green-700">
                    Contact support if you have additional questions
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = `mailto:support@${finalCompanyName.toLowerCase().replace(/\s+/g, '')}.com`}
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Contact Support
            </button>
            
            <button
              onClick={() => window.close()}
              className="w-full bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              Close Window
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-500">
            <p>Session completed at {new Date().toLocaleTimeString()}</p>
            <p className="mt-1">Thank you for choosing {finalCompanyName}</p>
          </div>
        </div>
      </div>
    </BrandingProvider>
  );
};